import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ClipboardList, Compass, Globe, GraduationCap, LifeBuoy, Lightbulb, MonitorPlay } from 'lucide-react';
import { useMedia } from '@/hooks/useMedia';
import { useStage } from './guide/stage';
import { solutions } from './site-content';
import './funnel.css';

// Owner copy (2026-10-16). Live stages exist in code: public entry + chat, briefs + CRM push,
// verified demo flows. Onboard, train and support are direction. Learn (insights, demand signals)
// is the return path, not a stage.
const stages = [
  [Globe, 'Engage', 'Visitors ask, get answers in context, and start a live session from your site.', 'GTM', 'live'],
  [ClipboardList, 'Qualify', 'Captures role, process and goals. Writes the brief, pushes it to your CRM.', 'GTM', 'live'],
  [MonitorPlay, 'Demonstrate', 'Drives your real product live, verified flows only, multi-lingual.', 'GTM', 'live'],
  [Compass, 'Onboard', 'Guides a new user to their first task inside your product.', 'GTM', solutions.training.availability],
  [GraduationCap, 'Train', 'Teaches teams the product, role by role, task by task.', 'GTM', solutions.training.availability],
  [LifeBuoy, 'Support', 'Grounded answers inside the product, with a human handoff that keeps the context.', 'GTM', solutions.support.availability],
] as const;
const LEARN = 'Recurring questions, objections and friction flow back to the roadmap.';

const STAGE = 3600; // ms per stage: travel then dwell
const TRAVEL = 1100; // runner movement between stages
const RETURN = 1400; // runner along the return path
const DRAW = 600; // line draw
const PAUSE = 8000; // after a hover or focus

type Active = number | 'learn';

export function FunnelSection() {
  const root = useRef<HTMLElement>(null);
  const rail = useRef<HTMLOListElement>(null);
  const cells = useRef<(HTMLLIElement | null)[]>([]);
  const desktop = useMedia('(min-width: 1001px)');
  const reduced = useMedia('(prefers-reduced-motion: reduce)');
  const animate = desktop && !reduced;
  const [started, setStarted] = useState(false);
  const [active, setActive] = useState<Active>(0);
  const [target, setTarget] = useState(0);
  const [returning, setReturning] = useState(false);
  const [returnKey, setReturnKey] = useState(0);
  const [jump, setJump] = useState(false);
  const [lefts, setLefts] = useState<number[]>([]);
  const [railW, setRailW] = useState(0);
  const idx = useRef(0);
  const pauseUntil = useRef(0);
  const stage = useStage();
  // Mr. Clippy's eyes track the runner (its destination stage, set when it departs), he glances at
  // the stage name on arrival; during the Learn return-loop Foxy thinks and he half-lids.
  useEffect(() => {
    if (!stage) return;
    const cell = cells.current[target];
    stage.react('.fx-funnel', 'clippy', { lookAt: cell?.querySelector('.fx-stage-dot') ?? cell ?? null });
  }, [stage, target]);
  useEffect(() => {
    if (!stage) return;
    if (active === 'learn') {
      // B14: the pupils follow the return line right → left, then a slow blink (half-lid)
      stage.react('.fx-funnel', 'clippy', { eyes: { mode: 'look', target: { x: -1, y: 0.3 }, key: 'learn-line' } });
      window.setTimeout(() => stage.react('.fx-funnel', 'clippy', { eyes: { mode: 'half-lid', key: 'learn' } }), 1000);
      window.setTimeout(() => stage.react('.fx-funnel', 'clippy', { nod: true }), 1300); // a small satisfied nod after the loop
      return;
    }
    const cell = cells.current[active];
    stage.react('.fx-funnel', 'clippy', { eyes: { mode: 'glance', key: `arrive-${active}-${Date.now()}`, target: { x: 1, y: 0 } }, lookAt: cell?.querySelector('h3, .fx-stage-name') ?? cell ?? null });
  }, [active, stage]);

  useEffect(() => {
    if (!animate) return;
    const el = root.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setStarted(true);
        io.disconnect();
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [animate]);

  useLayoutEffect(() => {
    const measure = () => {
      const r = rail.current;
      if (!r) return;
      const base = r.getBoundingClientRect().left;
      setRailW(r.getBoundingClientRect().width);
      setLefts(cells.current.map((c) => (c ? c.getBoundingClientRect().left - base + 6 : 0)));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [desktop]);

  // The walk: travel, arrive (active), dwell; after Support, the return path (Learn), then Engage.
  useEffect(() => {
    if (!animate || !started) return;
    let t = 0;
    const tick = () => {
      const wait = pauseUntil.current - Date.now();
      if (wait > 0) {
        t = window.setTimeout(tick, wait);
        return;
      }
      if (idx.current === stages.length - 1) {
        setReturning(true);
        setReturnKey((k) => k + 1);
        setActive('learn');
        t = window.setTimeout(() => {
          setReturning(false);
          setJump(true);
          idx.current = 0;
          setTarget(0);
          setActive(0);
          requestAnimationFrame(() => requestAnimationFrame(() => setJump(false)));
          t = window.setTimeout(tick, STAGE - TRAVEL);
        }, RETURN);
        return;
      }
      const next = idx.current + 1;
      setTarget(next);
      t = window.setTimeout(() => {
        if (Date.now() < pauseUntil.current) {
          t = window.setTimeout(tick, 0);
          return;
        }
        idx.current = next;
        setActive(next);
        t = window.setTimeout(tick, STAGE - TRAVEL);
      }, TRAVEL);
    };
    t = window.setTimeout(tick, DRAW + stages.length * 80 + STAGE - TRAVEL);
    return () => window.clearTimeout(t);
  }, [animate, started]);

  const select = (i: Active) => {
    if (i === 'learn') {
      idx.current = stages.length - 1;
      setTarget(stages.length - 1);
    } else {
      idx.current = i;
      setTarget(i);
    }
    setActive(i);
    setReturning(false);
    pauseUntil.current = Date.now() + PAUSE;
  };

  const x1 = lefts[0] ?? 0;
  const x6 = lefts[stages.length - 1] ?? 0;
  const returnPath = `M ${x6} 0 V 13 Q ${x6} 22 ${x6 - 9} 22 H ${x1 + 9} Q ${x1} 22 ${x1} 13 V 0`;
  const DetailIcon = active === 'learn' ? Lightbulb : stages[active][0];
  const detailText = active === 'learn' ? LEARN : stages[active][2];
  const cls = ['fx-funnel', 'fx-rv', animate ? 'animate' : 'static', started || !animate ? 'in' : '', returning ? 'returning' : '', active === 'learn' ? 'learn' : ''].filter(Boolean).join(' ');
  return (
    <section className={cls} id="solutions" ref={root}>
      <div className="container">
        <p className="fx-eyebrow">The platform</p>
        <h2>One expert, the whole funnel.</h2>
        <p className="fx-funnel-sub">
          From the first visit to everyday use. GTM gets the pipeline. Product
          gets the signal.
        </p>
        <ol className="fx-stages" ref={rail}>
          {stages.map(([Icon, name, text, who, availability], i) => (
            <li
              key={name}
              ref={(el) => {
                cells.current[i] = el;
              }}
              className={`fx-stage${i === active ? ' active' : ''}`}
              style={{ '--i': i } as React.CSSProperties}
            >
              <span className="fx-stage-dot" aria-hidden="true" />
              <Icon className="fx-stage-icon" size={18} aria-hidden="true" />
              <h3>
                <button
                  type="button"
                  aria-pressed={i === active}
                  onMouseEnter={() => select(i)}
                  onFocus={() => select(i)}
                  onClick={() => select(i)}
                >
                  {name}
                </button>
              </h3>
              {!desktop && <p className="fx-stage-desc">{text}</p>}
              <span className="fx-stage-meta">
                <span className="fx-chip who">{who}</span>
                <span className={`fx-chip ${availability === 'live' ? 'ok' : 'dir'}`}>{availability}</span>
              </span>
            </li>
          ))}
          {animate && lefts.length === stages.length && !returning && (
            <span
              className={`fx-runner${jump ? ' jump' : ''}`}
              style={{ transform: `translateX(${lefts[target] - 5}px)` }}
              aria-hidden="true"
            />
          )}
        </ol>
        <div className="fx-return">
          {railW > 0 && (
            <svg viewBox={`0 0 ${railW} 24`} width={railW} height="24" aria-hidden="true">
              <path id="fx-return-path" d={returnPath} />
              {animate && returning && (
                <circle r="5" className="fx-return-runner" key={returnKey}>
                  <animateMotion dur="1.4s" fill="freeze" calcMode="spline" keyTimes="0;1" keySplines="0.45 0.05 0.2 1">
                    <mpath href="#fx-return-path" />
                  </animateMotion>
                </circle>
              )}
            </svg>
          )}
          <button
            type="button"
            className="fx-return-label"
            aria-pressed={active === 'learn'}
            onMouseEnter={() => select('learn')}
            onFocus={() => select('learn')}
            onClick={() => select('learn')}
          >
            Learn · questions and friction flow back to Product
            <span className="fx-chip who">Product</span>
            <span className="fx-chip ok">live</span>
          </button>
        </div>
        {desktop && (
          <div className="fx-detail" aria-live="polite">
            <DetailIcon className="fx-detail-icon" size={22} aria-hidden="true" />
            <p className="fx-detail-text" key={String(active)}>
              {detailText}
            </p>
          </div>
        )}
        {!desktop && (
          <>
            <p className="fx-learn-note">
              <Lightbulb size={16} aria-hidden="true" />
              Learn · {LEARN}
            </p>
          </>
        )}
      </div>
    </section>
  );
}
